import { Redline } from 'redline-client-sdk';

import {
  AgreementsLoaded,
  AgreementsLoadFailed,
  AgreementPreviewViewed,
  AgreementPreviewClicked,
  PaymentButtonEvts,
  PaymentCodeRetrievalFailed,
  PixQRCodeRendered,
  DirectOfferLoaded,
  OffersLoadStarted,
  OffersLoadEnded,
  OffersLoadFailed,
  AgreementDetailLoadFailed,
  AgreementDetailLoaded,
  OfferCheckoutStarted,
  OfferCheckoutFailed,
  OfferCheckoutSubmitted,
  OfferAgreementErrored,
  OfferPaymentMethodSelected,
  OfferModalitySelected,
  OfferAgreementCreated,
  OfferInstallmentOptionSelected,
  OfferInstallmentDateSelected,
  AgreementViewed,
  PaymentRequestLoaded,
  PaymentRequestErrored,
  PaymentRetryStarted,
  InstallmentPaid,
  OfferAgreementTermAccepted,
  OfferDelivered,
  OfferClicked,
  OfferViewed,
  OfferSectionRendered,
  OfferModalViewed,
  OfferResumeLoaded,
  OfferResumeErrored,
  OfferModalitiesLoaded,
  OfferModalitiesErrored,
  OfferBankDataLoaded,
  OfferBankDataErrored,
} from './types';
import { controlEventSending, removeCtrlCookieOnEventError } from '../../helpers/send-events-by-session';
import { appsFlyerBridge } from '../appsFlyer';
import { getSessionID } from 'redline-client-sdk';
import { incrementEvent } from '@libs/utils/helpers/sessionFeatures';

export const renegkitEvents = (rl: Redline) => ({
  // offers

  /**
   * Roda quando checkout com link checkout= carrega a simulação através da sorting debts pro
   */
  directOfferLoaded: (props: DirectOfferLoaded) => {
    return rl.track('renegkit.DirectOfferLoaded.v1', props);
  },

  offersLoadStarted: (props: OffersLoadStarted) => {
    return rl.track('renegkit.OffersLoadStarted.v1', props);
  },

  offersLoadEnded: (props: OffersLoadEnded) => {
    return rl.track('renegkit.OffersLoadEnded.v1', props); // TODO - add debts fields when using renegkit listing
  },

  offersLoadFailed: (props: OffersLoadFailed) => {
    return rl.track('renegkit.OffersLoadFailed.v1', props);
  },

  // agreements
  agreementLoadEnded: () => {
    return rl.track('renegkit.AgreementLoadEnded.v1'); //TODO
  },

  // `isRkVariant` gates the renegkit.* event name behind the dx-rk-offers-listing
  // experiment; defaults to the legacy agreements.* events for everyone else.
  agreementsLoaded: (props: AgreementsLoaded, isRkVariant?: boolean) => {
    return rl.track(isRkVariant ? 'renegkit.AgreementsLoadEnded.v1' : 'agreements.AgreementsLoaded.v1', props);
  },

  agreementsLoadFailed: (props: AgreementsLoadFailed, isRkVariant?: boolean) => {
    return rl.track(isRkVariant ? 'renegkit.AgreementsLoadFailed.v1' : 'agreements.AgreementsLoadFailed.v1', props);
  },

  agreementPreviewViewed: (props: AgreementPreviewViewed, isRkVariant?: boolean) => {
    const AGREEMENTS_PREVIEW_COOKIE_KEY = 'rl_ctrl_agreements_preview';
    const idEvent = `${props.agreementPreviewContext.id}-${props.location}`;

    return controlEventSending(AGREEMENTS_PREVIEW_COOKIE_KEY, idEvent, () => {
      rl.track(isRkVariant ? 'renegkit.AgreementPreviewed.v1' : 'agreements.AgreementPreviewed.v1', props).catch((err) => {
        removeCtrlCookieOnEventError(AGREEMENTS_PREVIEW_COOKIE_KEY, idEvent, err);
      });
    });
  },

  agreementPreviewClicked: (props: AgreementPreviewClicked, isRkVariant?: boolean) => {
    rl.track(isRkVariant ? 'renegkit.AgreementPreviewClicked.v1' : 'agreements.AgreementPreviewClicked.v1', props);
  },

  agreementDetailLoaded: (props: AgreementDetailLoaded) => {
    rl.track('renegkit.AgreementDetailLoaded.v1', props);
  },

  agreementDetailLoadFailed: (props: AgreementDetailLoadFailed) => {
    rl.track('renegkit.AgreementDetailLoadFailed.v1', props);
  },

  // payments
  paymentButtonEnabled(props: PaymentButtonEvts) {
    // TODO change to renegkit.PaymentButtonEnabled.v1
    const CTRL_PAYMENTS_BTN_COOKIE_KEY = 'rl_ctrl_payment_btn';
    const pagePath = window.location.pathname;
    const idEvent = `payment-btn-enabled-${props.outcomeType}-${props.agreementId}-${props.paymentType}-${pagePath}`;

    return controlEventSending(CTRL_PAYMENTS_BTN_COOKIE_KEY, idEvent, () => {
      rl.track('payments.PaymentButtonEnabled.v1', props).catch((err) => {
        removeCtrlCookieOnEventError(CTRL_PAYMENTS_BTN_COOKIE_KEY, idEvent, err);
      });
    });
  },

  paymentButtonClicked(props: PaymentButtonEvts) {
    // TODO change to renegkit.PaymentButtonClicked.v1
    return rl.track('payments.PaymentButtonClicked.v1', props);
  },

  paymentCodeRetrieved(props: PaymentButtonEvts) {
    // TODO change to renegkit.PaymentCodeRetrieved.v1
    return rl.track('payments.PaymentCodeRetrieved.v1', props);
  },

  paymentCodeRetrievalFailed(props: PaymentCodeRetrievalFailed) {
    // TODO change to renegkit.PaymentCodeRetrievalFailed.v1
    return rl.track('payments.PaymentCodeRetrievalFailed.v1', props);
  },

  // TODO: remove - replaced with elementViewed
  pixQRCodeRendered(props: PixQRCodeRendered) {
    return rl.track('payments.PixQRCodeRendered.v1', props);
  },

  // offer delivered
  offerDelivered(props: OfferDelivered) {
    const CTRL_OFFER_DELIVERED_COOKIE_KEY = 'rl_ctrl_offer_delivered';
    const idEvent = `offer-delivered-${props.offerId}`;

    return controlEventSending(CTRL_OFFER_DELIVERED_COOKIE_KEY, idEvent, () => {
      rl.track('renegkit.OfferDelivered.v1', props).catch((err) => {
        removeCtrlCookieOnEventError(CTRL_OFFER_DELIVERED_COOKIE_KEY, idEvent, err);
      });
    });
  },

  // offers experience UI events

  /** renegkit.OfferClicked.v1 — replaces debts.DebtClicked.v1. */
  offerClicked(props: OfferClicked) {
    return rl.track('renegkit.OfferClicked.v1', props);
  },

  /** renegkit.OfferViewed.v1 — replaces debts.DebtViewed.v1. */
  offerViewed(props: OfferViewed) {
    return rl.track('renegkit.OfferViewed.v1', props);
  },

  /** renegkit.OfferSectionRendered.v1 — replaces userTracking.ElementClicked.v1. */
  offerSectionRendered(props: OfferSectionRendered) {
    return rl.track('renegkit.OfferSectionRendered.v1', props);
  },

  /** renegkit.OfferModalViewed.v1 */
  offerModalViewed(props: OfferModalViewed) {
    return rl.track('renegkit.OfferModalViewed.v1', props);
  },

  // offers experience observability events
  offerResumeLoaded(props: OfferResumeLoaded) {
    return rl.track('renegkit.OfferResumeLoaded.v1', props);
  },

  offerResumeErrored(props: OfferResumeErrored) {
    return rl.track('renegkit.OfferResumeErrored.v1', props);
  },

  offerModalitiesLoaded(props: OfferModalitiesLoaded) {
    return rl.track('renegkit.OfferModalitiesLoaded.v1', props);
  },

  offerModalitiesErrored(props: OfferModalitiesErrored) {
    return rl.track('renegkit.OfferModalitiesErrored.v1', props);
  },

  offerBankDataLoaded(props: OfferBankDataLoaded) {
    return rl.track('renegkit.OfferBankDataLoaded.v1', props);
  },

  offerBankDataErrored(props: OfferBankDataErrored) {
    return rl.track('renegkit.OfferBankDataErrored.v1', props);
  },

  // offer checkout
  offerCheckoutStarted(props: OfferCheckoutStarted) {
    const event = 'renegkit.OfferCheckoutStarted.v1';
    const c = props.creditor;
    const key = c?.slug ?? '';
    incrementEvent('n_started', key, getSessionID());

    appsFlyerBridge().logEvent('CheckoutStarted', props);

    return rl.track(event, props);
  },

  offerCheckoutFailed(props: OfferCheckoutFailed) {
    return rl.track('renegkit.OfferCheckoutFailed.v1', props);
  },

  offerCheckoutSubmitted(props: OfferCheckoutSubmitted) {
    return rl.track('renegkit.OfferCheckoutSubmitted.v1', props);
  },

  offerAgreementCreated(props: OfferAgreementCreated) {
    const event = 'renegkit.OfferAgreementCreated.v1';

    appsFlyerBridge().logEvent('AgreementCreated', props);
    appsFlyerBridge().logEvent('partner_AgreementOutcomeReceived_v1', {});
    appsFlyerBridge().logEvent('partner_AgreementCreated', {});

    return rl.track(event, props);
  },

  offerAgreementErrored(props: OfferAgreementErrored) {
    return rl.track('renegkit.OfferAgreementErrored.v1', props);
  },

  offerPaymentMethodSelected(props: OfferPaymentMethodSelected) {
    return rl.track('renegkit.OfferPaymentMethodSelected.v1', props);
  },

  offerModalitySelected(props: OfferModalitySelected) {
    return rl.track('renegkit.OfferModalitySelected.v1', props);
  },

  offerInstallmentOptionSelected(props: OfferInstallmentOptionSelected) {
    return rl.track('renegkit.OfferInstallmentOptionSelected.v1', props);
  },

  offerInstallmentDateSelected(props: OfferInstallmentDateSelected) {
    return rl.track('renegkit.OfferInstallmentDateSelected.v1', props);
  },

  offerAgreementTermAccepted(props: OfferAgreementTermAccepted) {
    return rl.track('renegkit.OfferAgreementTermAccepted.v1', props);
  },

  // closed agreement
  agreementViewed(props: AgreementViewed) {
    return rl.track('renegkit.AgreementViewed.v1', props);
  },

  // payment request
  paymentRequestLoaded(props: PaymentRequestLoaded) {
    return rl.track('renegkit.PaymentRequestLoaded.v1', props);
  },

  paymentRequestErrored(props: PaymentRequestErrored) {
    return rl.track('renegkit.PaymentRequestErrored.v1', props);
  },

  paymentRetryStarted(props: PaymentRetryStarted) {
    return rl.track('renegkit.PaymentRetryStarted.v1', props);
  },

  installmentPaid(props: InstallmentPaid) {
    return rl.track('renegkit.InstallmentPaid.v1', props);
  },
});
